import { useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { useConfirm, useToast } from "@/components/ui/feedback";
import { Modal, ModalFooter } from "@/components/ui/modal";
import {
	useBankAccounts,
	useCreateBankAccount,
	useDeleteBankAccount,
	useUpdateBankAccount,
} from "@/hooks/use-bank-accounts";
import type {
	BankAccount,
	UpsertBankAccountInput,
} from "@/lib/payments/bank-accounts-api";

/**
 * Comptes bancaires proposés au client qui paie par virement.
 *
 * La page de confirmation demandait un virement sans dire vers quel compte :
 * le client n'avait aucun moyen de payer sans appeler le service client. Les
 * comptes actifs y sont désormais affichés, dans l'ordre défini ici.
 */
export function AdminBankAccountsPage() {
	const { data: comptes = [], isLoading } = useBankAccounts();
	const del = useDeleteBankAccount();
	const update = useUpdateBankAccount();
	const confirm = useConfirm();
	const toast = useToast();
	const [edite, setEdite] = useState<BankAccount | null>(null);
	const [cree, setCree] = useState(false);

	const actifs = comptes.filter((c) => c.isActive).length;

	const basculer = async (c: BankAccount) => {
		try {
			await update.mutateAsync({ id: c.id, body: { isActive: !c.isActive } });
			toast.success(c.isActive ? "Compte retiré du checkout" : "Compte proposé au checkout");
		} catch {
			toast.error("Échec de la mise à jour");
		}
	};

	const supprimer = async (c: BankAccount) => {
		const ok = await confirm({
			title: `Supprimer le compte ${c.bankName} ?`,
			message:
				"Des commandes en attente de virement peuvent encore renvoyer à ce compte. " +
				"Préférez la désactivation, qui le retire du checkout sans effacer l'historique.",
			tone: "danger",
			confirmLabel: "Supprimer quand même",
			requireText: c.bankName,
			requireHint: (
				<>
					Tapez <strong>{c.bankName}</strong> pour confirmer
				</>
			),
		});
		if (!ok) return;
		try {
			await del.mutateAsync(c.id);
			toast.success("Compte supprimé");
		} catch {
			toast.error("Échec de la suppression");
		}
	};

	return (
		<>
			<PageHead
				title="Comptes bancaires"
				sub="Comptes proposés au client qui règle par virement — affichés dans l'ordre défini ici"
				actions={
					<AdminButton variant="primary" onClick={() => setCree(true)}>
						<AdminIcon n="plus" s={14} /> Ajouter un compte
					</AdminButton>
				}
			/>

			{!isLoading && actifs === 0 && (
				<Card style={{ marginBottom: 16 }}>
					<CardBody>
						<div className="empty">
							Aucun compte actif : la page de confirmation invite le client à
							appeler le service client au lieu d'afficher un RIB. Ajoutez-en un
							pour que les virements soient payables en autonomie.
						</div>
					</CardBody>
				</Card>
			)}

			<Card>
				<CardHead
					title={`Comptes (${comptes.length})`}
					sub={`${actifs} proposé${actifs > 1 ? "s" : ""} au checkout`}
				/>
				<div className="table-wrap" style={{ margin: 0 }}>
					<table className="table">
						<thead>
							<tr>
								<th>Banque</th>
								<th>Titulaire</th>
								<th>Numéro de compte</th>
								<th>SWIFT</th>
								<th>Ordre</th>
								<th>Statut</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{isLoading && (
								<tr>
									<td colSpan={7} className="muted center" style={{ padding: 24 }}>
										Chargement…
									</td>
								</tr>
							)}
							{!isLoading && comptes.length === 0 && (
								<tr>
									<td colSpan={7} className="muted center" style={{ padding: 24 }}>
										Aucun compte enregistré.
									</td>
								</tr>
							)}
							{comptes.map((c) => (
								<tr key={c.id}>
									<td>
										<strong>{c.bankName}</strong>
										{c.note && (
											<div className="muted" style={{ fontSize: 12 }}>
												{c.note}
											</div>
										)}
									</td>
									<td className="muted">{c.holder}</td>
									<td className="tn">{c.accountNumber}</td>
									<td className="muted">{c.swift ?? "—"}</td>
									<td className="tn muted">{c.sort}</td>
									<td>
										<Tag variant={c.isActive ? "success" : "neutral"}>
											{c.isActive ? "Proposé" : "Retiré"}
										</Tag>
									</td>
									<td className="right" style={{ whiteSpace: "nowrap" }}>
										<AdminButton variant="ghost" size="sm" onClick={() => basculer(c)}>
											{c.isActive ? "Retirer" : "Proposer"}
										</AdminButton>
										<AdminButton
											variant="ghost"
											size="sm"
											iconOnly
											onClick={() => setEdite(c)}
											aria-label="Modifier"
											style={{ marginLeft: 4 }}
										>
											<AdminIcon n="edit" s={14} />
										</AdminButton>
										<AdminButton
											variant="ghost"
											size="sm"
											iconOnly
											onClick={() => supprimer(c)}
											aria-label="Supprimer"
											style={{ marginLeft: 4 }}
										>
											<AdminIcon n="close" s={14} />
										</AdminButton>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</Card>

			{(cree || edite) && (
				<BankAccountFormModal
					compte={edite}
					onClose={() => {
						setCree(false);
						setEdite(null);
					}}
				/>
			)}
		</>
	);
}

function BankAccountFormModal({
	compte,
	onClose,
}: {
	compte: BankAccount | null;
	onClose: () => void;
}) {
	const create = useCreateBankAccount();
	const update = useUpdateBankAccount();
	const toast = useToast();

	const [bankName, setBankName] = useState(compte?.bankName ?? "");
	const [holder, setHolder] = useState(compte?.holder ?? "KEVMAX SARL");
	const [accountNumber, setAccountNumber] = useState(compte?.accountNumber ?? "");
	const [iban, setIban] = useState(compte?.iban ?? "");
	const [swift, setSwift] = useState(compte?.swift ?? "");
	const [note, setNote] = useState(compte?.note ?? "");
	const [sort, setSort] = useState(String(compte?.sort ?? 0));
	const [isActive, setIsActive] = useState(compte?.isActive ?? true);

	const pending = create.isPending || update.isPending;
	const valid =
		bankName.trim().length > 1 &&
		holder.trim().length > 1 &&
		accountNumber.trim().length > 1;

	const submit = async () => {
		if (!valid) return;
		const body: UpsertBankAccountInput = {
			bankName: bankName.trim(),
			holder: holder.trim(),
			accountNumber: accountNumber.trim(),
			iban: iban.trim() || null,
			swift: swift.trim() || null,
			note: note.trim() || null,
			sort: Number(sort) || 0,
			isActive,
		};
		try {
			if (compte) await update.mutateAsync({ id: compte.id, body });
			else await create.mutateAsync(body);
			toast.success(compte ? "Compte mis à jour" : "Compte ajouté");
			onClose();
		} catch (e) {
			toast.error((e as Error).message || "Échec de l’enregistrement");
		}
	};

	return (
		<Modal
			title={compte ? `Modifier ${compte.bankName}` : "Nouveau compte bancaire"}
			subtitle="Ces coordonnées s'affichent telles quelles sur la confirmation de commande"
			onClose={onClose}
			width={560}
		>
			<div style={{ display: "grid", gap: 12 }}>
				<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
					<div>
						<label className="field-label">Banque *</label>
						<input
							className="aform-i"
							value={bankName}
							onChange={(e) => setBankName(e.target.value)}
							placeholder="Ex. Afriland First Bank"
						/>
					</div>
					<div>
						<label className="field-label">Titulaire du compte *</label>
						<input
							className="aform-i"
							value={holder}
							onChange={(e) => setHolder(e.target.value)}
							placeholder="KEVMAX SARL"
						/>
					</div>
				</div>

				<div>
					<label className="field-label">Numéro de compte / RIB *</label>
					<input
						className="aform-i"
						value={accountNumber}
						onChange={(e) => setAccountNumber(e.target.value)}
						placeholder="10005 00024 06735511001 21"
					/>
					<p className="field-help" style={{ margin: "4px 0 0" }}>
						Recopiez-le tel qu'il se saisit au guichet : code banque, code
						guichet, numéro de compte et clé RIB. C'est ce que le client
						copiera.
					</p>
				</div>

				<div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
					<div>
						<label className="field-label">IBAN</label>
						<input
							className="aform-i"
							value={iban}
							onChange={(e) => setIban(e.target.value)}
							placeholder="CM21 …"
						/>
					</div>
					<div>
						<label className="field-label">Code SWIFT / BIC</label>
						<input
							className="aform-i"
							value={swift}
							onChange={(e) => setSwift(e.target.value)}
							placeholder="CCEICMCX"
						/>
					</div>
				</div>

				<div>
					<label className="field-label">Précision affichée au client</label>
					<input
						className="aform-i"
						value={note}
						onChange={(e) => setNote(e.target.value)}
						placeholder="Ex. Virement interne gratuit pour les clients Afriland"
					/>
				</div>

				<div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
					<div>
						<label className="field-label">Ordre d'affichage</label>
						<input
							className="aform-i"
							type="number"
							value={sort}
							onChange={(e) => setSort(e.target.value)}
						/>
						<p className="field-help" style={{ margin: "4px 0 0" }}>
							Le plus petit s'affiche en premier.
						</p>
					</div>
					<div style={{ alignSelf: "center" }}>
						<label className="checkbox">
							<input
								type="checkbox"
								checked={isActive}
								onChange={(e) => setIsActive(e.target.checked)}
							/>
							Proposer ce compte au checkout
						</label>
					</div>
				</div>

				{!valid && (
					<p className="field-help error" style={{ margin: 0 }}>
						Banque, titulaire et numéro de compte sont obligatoires : sans eux,
						un virement n'arriverait nulle part.
					</p>
				)}
			</div>

			<ModalFooter>
				<AdminButton variant="ghost" onClick={onClose}>
					Annuler
				</AdminButton>
				<AdminButton variant="primary" onClick={submit} disabled={!valid || pending}>
					{pending ? "Enregistrement…" : compte ? "Enregistrer" : "Ajouter"}
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}
